import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import PropTypes from "prop-types";

import DeleteModal from "../../UI/Modal/DeleteModal";
import Button from "../../UI/Button/Button";
import ApplicationSearchBar from "../Applications/ApplicationSearchBar";
import EmptyState from "../../UI/State/EmptyState";

import * as SandBoxApplicationsActions from "../../../middleware/Store/Actions/SandBoxApplicationsActions";
import * as ContactUtils from "../../Utils/ContactUtils";
import * as PathUtils from "../../Utils/PathUtils";
import * as ApplicationsUtils from "../../Utils/ApplicationsUtils";
import * as ApplicationConstant from "../../Constants/ApplicationConstant";

import { faTrashAlt, faPencilAlt, faSort, faRocket, faExclamation, faSortAlphaDown, faSortAlphaUp, faDownload, faSyncAlt, faExclamationTriangle} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import LogService from "../../../middleware/CommonSdk/LogService";

const LOG_TAG = "SANDBOX_APPLICATION_LIST";
const defaultDataField = "dateOfCreation";

const SandBoxApplicationsList = (props) => {
    const intl = useIntl();
    const history = useHistory();
    const [itemsSelected, setItemsSelected] = useState([]);
    const [modal, setModal] = useState(null);
    const [applicationReady, setApplicationReady] = useState(false);

    useEffect(() => {
        LogService.debug(LOG_TAG, "useEffect applications retrig ", props.applications.length > 0);
        setApplicationReady(props.applications.length > 0);
    }, [props.applications]);

    // No data displayed
    const noDataFormatter = () => {
        let display =
            <EmptyState
                icon={faRocket}
                text={intl.messages.applicationsMessageNoApplicationFound}
            />
        return display;
    }

    // Status ... RUNNING, STOPPER...
    const stateFormatter = (cell, row) => {
        const { label, className } = ApplicationsUtils.getApplicationStateAndStyle(row);
        return <span className={"badge-dashed small " + className}>{label}</span>
    }

    const csvStateFormatter = (cell, row) => {
        const { label, className } = ApplicationsUtils.getApplicationStateAndStyle(row);
        return label;
    }
    
    const caretFormatter = (order) => {
        if (!order) return (<span><FontAwesomeIcon icon={faSort} className="ml-2" /></span>);
        else if (order === "asc") return (<span><FontAwesomeIcon icon={faSortAlphaDown} className="ml-2" /></span>);
        else if (order === "desc") return (<span><FontAwesomeIcon icon={faSortAlphaUp} className="ml-2" /></span>);
        return null;
    }

    // Date of creation
    const dateFormatter = (date) => {
        return intl.formatDate(date, { year: "numeric", month: "short", day: "numeric" })
    }

    // Information of creator... Name, OwnerId and Company
    const ownerFormatter = (ownerId, app, rowIndex, extraData) => {
        const foundContact = ContactUtils.getContactById(app.ownerId, extraData);
        const companyName = ContactUtils.getCompanyName(foundContact);
        const displayName = ContactUtils.getDisplayName(foundContact) || app.ownerId;
        const isStillDeveloper = ContactUtils.isDeveloper(foundContact);
        
        return (
            <div>
                <div className="row">
                    <div className="col-md-12">{displayName}
                        {!isStillDeveloper &&
                            <span className="ml-1" data-tooltip data-placement="left" title={intl.messages.applicationIsNoLongerDeveloperMessage}>
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning" size="sm" />
                            </span>
                        }
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12 d-none d-lg-block"><small>{app.ownerId}</small></div>
                </div>
                <div className="row">
                    <div className="col-md-12 d-none d-lg-block"><small>{companyName}</small></div>
                </div>
            </div>
        );
    }

    // Application name and id
    const nameFormatter = (cell, row) => {
        return (
            <>
                <div className="row m-0">
                    <div className="col-md-12">{row.name}</div>
                </div>
                <div className="row m-0">
                    <div className="col-md-12 d-none d-lg-block"><small>{row.id}</small></div>
                </div>
            </>
        )
    }

    const csvOwnerFormatter = (ownerId, app, rowIndex, extraData) => {
        const foundContact = ContactUtils.getContactById(app.ownerId, extraData);
        return ContactUtils.getDisplayName(foundContact) || app.ownerId;
    }

    const csvOwnerCompanyNameFormatter = (ownerId, app, rowIndex, extraData) => {
        const foundContact = ContactUtils.getContactById(app.ownerId, extraData);
        return ContactUtils.getCompanyName(foundContact);
    }

    const csvDateFormatter = (date) => {
        return intl.formatDate(date, { year: "numeric", month: "numeric", day: "numeric" })
    }

    const actionFormatter = (cell, app, rowIndex, extraData) => {
        let isMyApplication = ApplicationsUtils.isMyApplication(app, props.sandBoxUser);
        let disableActions = extraData === true;

        let ownerActions = [];
        if (isMyApplication || props.isCompanyAdmin) {
            ownerActions.push(
                <Button
                    key="ownerActions0"
                    targetName="#deleteSingleApplicationModal"
                    icon={faTrashAlt}
                    className="btn-grid ml-1"
                    onClick={() => handleTableActionDelete(app.id)}
                    tooltipTitle={intl.messages.delete}
                    disabled={disableActions}
                />
            );
        }

        return (<span>
            <Button
                icon={faPencilAlt}
                className="btn-grid ml-1"
                onClick={() => handleTableActionEdit(app.id)}
                tooltipTitle={intl.messages.edit}
                disabled={disableActions}
            />
            {ownerActions}
        </span>)
    }

    const handleOnSelect = (row, isSelect) => {
        LogService.trace(LOG_TAG, "handleOnSelect");
        if (isSelect) {
            setItemsSelected([...itemsSelected, row.id]);
        } else {
            setItemsSelected(itemsSelected.filter((appId) => (appId !== row.id)));
        }
    }

    const handleOnSelectAll = (isSelect, rows) => {
        LogService.trace(LOG_TAG, "handleOnSelectAll");
        const ids = rows.map(app => app.id);

        if (isSelect) {
            setItemsSelected(ids);
        } else {
            clearItemsSelected();
        }
    }

    const clearItemsSelected = () => {
        LogService.trace(LOG_TAG, "clearItemsSelected");
        setItemsSelected([]);
    }

    const handleTableActionEdit = (appId) => {
        LogService.trace(LOG_TAG, "handleTableActionEdit");
        let pathUrl = PathUtils.addOrUpdateQueryParameterToPathString(ApplicationConstant.APPLICATION_PATH + ApplicationConstant.EDIT_PATH, ApplicationConstant.ApplicationTagName, appId);
        pathUrl = PathUtils.addOrUpdateQueryParameterToPathString(pathUrl, ApplicationConstant.ApplicationSandBoxTagName, true);
        history.push(pathUrl);
    };

    const handleTableActionDelete = (appId) => {
        LogService.trace(LOG_TAG, "handleTableActionDelete");
        handleGroupActionDelete([appId]);
    };

    const handleDeleteApplications = (applicationIdList) => {
        LogService.trace(LOG_TAG, "handleDeleteApplication");

        if (applicationIdList && applicationIdList.length > 0) {
            props.deleteApplications(applicationIdList)
                .then(() => {
                    LogService.debug(LOG_TAG, "handleDeleteApplication success");
                    clearItemsSelected();
                });
        }
    };

    const handleApplicationsRefresh = () => {
        LogService.trace(LOG_TAG, "handleApplicationsRefresh");
        if (props.onApplicationsRefresh) {
            props.onApplicationsRefresh();
        }
    };

    const handleGroupActionDelete = (applicationIdList) => {
        LogService.trace(LOG_TAG, "handleGroupActionDelete");

        const description = intl.formatMessage({ id: "applicationActionDeleteMessage" }, { value: applicationIdList.length });

        setModal(
            <DeleteModal
                title={intl.messages.applicationActionDeleteTitle}
                icon={faExclamation}
                btnlabel={intl.messages.delete}
                onClick={() => handleDeleteApplications(applicationIdList)}
                showModalOnInit={true}
            >
                <p>{description}</p>
                <div className="list">{ApplicationsUtils.getApplicationListNames(applicationIdList, props.applications)}</div>
            </DeleteModal>
        );
    };

    LogService.trace(LOG_TAG, "render");

    const displaySearchAndFilter = props.applications.length > ApplicationConstant.DisplaySearchAndFilterCount;
    const diplayGroupActions = props.applications.length > 0;
    const hideHeaderClassName = props.applications.length > 0 ? "" : " d-none";

    const selectRow = {
        mode: "checkbox",
        classes: "bg-fog",
        onSelect: handleOnSelect,
        onSelectAll: handleOnSelectAll,
        selected: itemsSelected
    };

    const columns = [{
        dataField: "id",//used for csv export
        text: "Application id",
        hidden: true
    }, {
        dataField: "name",
        text: intl.messages.applicationName,
        headerStyle: {paddingLeft:'15px'},
        formatter: nameFormatter,
        sort: true,
        sortCaret: caretFormatter,
        searchable: true
    }, {
        dataField: "ownerName",//fake field
        isDummyField: true,
        text: intl.messages.applicationOwner,
        align: "center",
        headerAlign: "center",
        sort: true,
        sortCaret: caretFormatter,
        sortFunc: (a, b, order, dataField, appA, appB) => {
            const foundContactA = ContactUtils.getContactById(appA.ownerId, [...props.contacts, props.sandBoxUser]);
            const displayNameA = ContactUtils.getDisplayName(foundContactA) || appA.id;
            const foundContactB = ContactUtils.getContactById(appB.ownerId, [...props.contacts, props.sandBoxUser]);
            const displayNameB = ContactUtils.getDisplayName(foundContactB) || appB.id;

            if (displayNameA === displayNameB) {
                return 0;
            } else if (displayNameB < displayNameA) {
                return order === "asc" ? 1 : -1;
            } else {
                return order === "asc" ? -1 : 1;
            }
        },
        searchable: false,
        formatter: ownerFormatter,
        csvFormatter: csvOwnerFormatter,
        formatExtraData: [...props.contacts, props.sandBoxUser],
    }, {
        dataField: "ownerId", //used for csv export
        text: intl.messages.applicationOwnerId,
        hidden: true,
    }, {//used only for csv export
        dataField: "ownerCompany",//fake field
        isDummyField: true,
        text: intl.messages.applicationOwnerCompanyName,
        hidden: true,
        csvFormatter: csvOwnerCompanyNameFormatter,
        formatExtraData: [...props.contacts, props.sandBoxUser],
    },
    {
        dataField: "env or state",//fake field
        isDummyField: true,
        text: intl.messages.applicationState,
        align: "center",
        headerAlign: "center",
        sort: true,
        sortCaret: caretFormatter,
        sortFunc: (a, b, order, dataField, appA, appB) => {
            const labelObjectA = ApplicationsUtils.getApplicationStateAndStyle(appA);
            const labelObjectB = ApplicationsUtils.getApplicationStateAndStyle(appB);

            if (labelObjectA.label === labelObjectB.label) {
                return 0;
            } else if (labelObjectB.label < labelObjectA.label) {
                return order === "asc" ? 1 : -1;
            } else {
                return order === "asc" ? -1 : 1;
            }
        },
        searchable: false,
        formatter: stateFormatter,
        csvFormatter: csvStateFormatter,
    }, {
        dataField: "dateOfCreation",
        text: intl.messages.applicationCreatedOn,
        align: "center",
        headerAlign: "center",
        sort: true,
        sortCaret: caretFormatter,
        searchable: false,
        formatter: dateFormatter,
        csvFormatter: csvDateFormatter,
    }, {
        dataField: "actions",//fake field
        isDummyField: true,
        text: intl.messages.actions,
        align: "center",
        headerAlign: "center",
        headerStyle: {
            width: "70px"
        },
        searchable: false,
        formatter: actionFormatter,
        formatExtraData: itemsSelected.length > 1,
        csvExport: false
    }];

    const ExportCSV = (props) => {
        const handleClick = () => {
            props.onExport();
        };
        return (
            <Button
                id="btnGroupExportCsv"
                icon={faDownload}
                className="btn btn-groups"
                onClick={handleClick}
                tooltipTitle={intl.messages.applicationGroupActionExportButtonTooltip}
            />
        );
    };

    let groupButton = [];
    if (itemsSelected.length > 0) {
        const areMyApplications = ApplicationsUtils.areOnlyMyApplications(props.sandBoxUser.id, props.applications, itemsSelected);
        const enableDelete = props.isCompanyAdmin || areMyApplications;
        groupButton.push(
            <Button
                key="groupButton0"
                id="btnGroupDelete"
                icon={faTrashAlt}
                className="btn btn-groups"
                onClick={() => handleGroupActionDelete(itemsSelected)}
                tooltipTitle={enableDelete ? intl.messages.applicationGroupActionDeleteButtonTooltip : intl.messages.applicationGroupActionDeleteButtonTooltipDisabled}
                disabled={!enableDelete}
            />
        );
    }

    return (
        <React.Fragment>
            {modal}
            <ToolkitProvider
                keyField="id"
                data={props.applications}
                columns={columns}
                search
                columnToggle
                exportCSV={{
                    fileName: 'sandbox.csv',
                    separator: ';',
                    exportAll: false,
                    // onlyExportSelection: itemsSelected.length > 0
                    onlyExportFiltered: itemsSelected.length > 0
                }}
                bootstrap4={true}
            >
                {
                    tableprops => (
                        <React.Fragment>
                            <div className="d-flex align-items-end" role="toolbar" aria-label="Toolbar with button groups">
                                {displaySearchAndFilter &&
                                    <ApplicationSearchBar {...tableprops.searchProps}
                                        reTriggerSearch={applicationReady}
                                        showFilter={false}
                                    />
                                }
                                {diplayGroupActions &&
                                    <div className="btn-group mb-3 ml-auto" role="group" >
                                        <ExportCSV {...tableprops.csvProps} />
                                        <Button
                                            icon={faSyncAlt}
                                            className="btn btn-groups"
                                            onClick={handleApplicationsRefresh}
                                            tooltipTitle={intl.messages.applicationGroupActionRefreshButtonTooltip}
                                        />
                                        {groupButton}
                                    </div>
                                }
                            </div>

                            <div className="sandBox-table maxHeight overflow-auto">
                                <BootstrapTable
                                    id="sandbox-table"
                                    bordered={false}
                                    bodyClasses="sandBox-table-body"
                                    headerClasses={"sandBox-table-header " + hideHeaderClassName}
                                    selectRow={selectRow}
                                    noDataIndication={noDataFormatter}
                                    defaultSorted={[{
                                        dataField: defaultDataField, // if dataField is not match to any column you defined, it will be ignored.
                                        order: "desc" // desc or asc
                                    }]}
                                    {...tableprops.baseProps}
                                />
                            </div>
                        </React.Fragment>
                    )
                }
            </ToolkitProvider>
        </React.Fragment>
    );
}

SandBoxApplicationsList.defaultProps = {
    onApplicationsRefresh: null,
}

SandBoxApplicationsList.propTypes = {
    onApplicationsRefresh: PropTypes.func,
};

const mapStateToProps = state => {
    return {
        sandBoxUser: state.sandBoxContacts.user,
        applications: state.sandBoxApplications.applications,
        contacts: state.sandBoxContacts.contacts,
        isCompanyAdmin: ContactUtils.isCompanyAdmin(state.sandBoxContacts.user)
    };
};

const mapDispatchToProps = dispatch => {
    return {
        deleteApplications: (appIdList) => dispatch(SandBoxApplicationsActions.deleteApplications(appIdList)),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(SandBoxApplicationsList);
